// graft(#97c): copied from genome/v4 sdk/src/router/route-resolution.ts — v5 owns this module, no live dep on v3/v4.
/**
 * Genome route resolution — routes come from THE genome, editors come from
 * the composed plugins, and an unresolvable target is a composition-time
 * error surfaced by the one-genome gate test, never a runtime not-found
 * fallback.
 *
 * Editor resolution contract (see plugins/contract.ts): a route's
 * `routes-to` target `{ns}:component/{id}` resolves to `editors[id]` of
 * the plugin whose `id` (or declared `contributesTo`) owns `{ns}`. No
 * candidate-key probing.
 *
 * Lifted from apps/studio in Phase 1 — the app now consumes
 * `@ubiclouds/v5-sdk/router`.
 */

import type { ComponentType } from 'react';
import type { EntityDef, GenomeV3 } from '../genome/types.js';
import type { ComposableGenomePlugin } from '../plugins/contract.js';

export interface ResolvedRoute {
  /** Full entity path of the route entity, e.g. 'v4-welcome:route/home'. */
  readonly entityPath: string;
  /** URL path from the route entity's meta, e.g. '/'. */
  readonly path: string;
  readonly title?: string;
  readonly Component: ComponentType;
}

export class RouteResolutionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RouteResolutionError';
  }
}

function componentTarget(entity: EntityDef): string | undefined {
  return entity.refs?.find((r) => r.rel === 'routes-to')?.target;
}

/**
 * Resolve every `route` entity in the genome to its editor component.
 * Throws on the first unresolvable route — a route without a component is
 * a composition bug, never a user-facing 404.
 */
export function resolveRoutes(
  genome: GenomeV3,
  plugins: readonly ComposableGenomePlugin[],
): ResolvedRoute[] {
  const routes: ResolvedRoute[] = [];

  for (const [entityPath, entity] of Object.entries(genome.entities)) {
    if (entity.kind !== 'route') continue;

    const target = componentTarget(entity);
    if (!target) {
      throw new RouteResolutionError(`route "${entityPath}" has no routes-to ref`);
    }

    const colonIdx = target.indexOf(':');
    const slashIdx = target.indexOf('/');
    if (colonIdx <= 0 || slashIdx <= colonIdx) {
      throw new RouteResolutionError(
        `route "${entityPath}" targets "${target}" — expected "{ns}:component/{id}"`,
      );
    }
    const ns = target.slice(0, colonIdx);
    const componentId = target.slice(slashIdx + 1);

    const plugin = plugins.find(
      (p) => p.id === ns || (p.contributesTo ?? []).includes(ns),
    );
    if (!plugin) {
      throw new RouteResolutionError(
        `route "${entityPath}" targets namespace "${ns}" but no composed plugin owns it`,
      );
    }

    const Component = plugin.editors?.[componentId];
    if (!Component) {
      throw new RouteResolutionError(
        `route "${entityPath}" targets editor "${componentId}" of plugin "${plugin.id}" — ` +
        `registered editors: [${Object.keys(plugin.editors ?? {}).join(', ')}]`,
      );
    }

    const path = entity.meta?.['path'];
    if (typeof path !== 'string' || !path.startsWith('/')) {
      throw new RouteResolutionError(`route "${entityPath}" has no meta.path`);
    }

    routes.push({
      entityPath,
      path,
      title: entity.meta?.['title'] as string | undefined,
      Component,
    });
  }

  return routes;
}
